/**
 * <prelaunch-countdown> — a self-contained countdown timer for the
 * pre-launch password page.
 *
 * Reads its target from `data-target` (an ISO-ish local datetime string,
 * e.g. "2026-06-18T18:00:00"). Every second it fills the child spans
 * marked with [data-days] / [data-hours] / [data-minutes] / [data-seconds].
 * When the target passes it stops the tick, adds the `is-expired` class,
 * and reveals the [data-expired] message (if present).
 */
class PrelaunchCountdown extends HTMLElement {
  connectedCallback() {
    this.target = this.parseTarget(this.dataset.target);
    this.units = {
      days: this.querySelector('[data-days]'),
      hours: this.querySelector('[data-hours]'),
      minutes: this.querySelector('[data-minutes]'),
      seconds: this.querySelector('[data-seconds]'),
    };
    this.expiredEl = this.querySelector('[data-expired]');

    if (!this.target || Number.isNaN(this.target.getTime())) {
      // No valid date configured — leave the static markup untouched.
      return;
    }

    this.tick = this.tick.bind(this);
    this.tick();
    this.interval = setInterval(this.tick, 1000);
  }

  disconnectedCallback() {
    if (this.interval) clearInterval(this.interval);
  }

  // Accept "2026-06-18 18:00" or "2026-06-18T18:00:00". Normalise the
  // space to a T so Safari parses it, and treat it as local time.
  parseTarget(value) {
    if (!value) return null;
    return new Date(value.trim().replace(' ', 'T'));
  }

  tick() {
    const remaining = this.target.getTime() - Date.now();

    if (remaining <= 0) {
      this.render(0, 0, 0, 0);
      this.expire();
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.render(days, hours, minutes, seconds);
  }

  render(days, hours, minutes, seconds) {
    this.set(this.units.days, days);
    this.set(this.units.hours, hours);
    this.set(this.units.minutes, minutes);
    this.set(this.units.seconds, seconds);
  }

  set(el, value) {
    if (el) el.textContent = String(value).padStart(2, '0');
  }

  expire() {
    if (this.interval) clearInterval(this.interval);
    this.classList.add('is-expired');
    if (this.expiredEl) this.expiredEl.hidden = false;
    this.dispatchEvent(new CustomEvent('prelaunch:countdown:expired', { bubbles: true }));
  }
}

if (!customElements.get('prelaunch-countdown')) {
  customElements.define('prelaunch-countdown', PrelaunchCountdown);
}
